import { invoke } from "@tauri-apps/api/core";
import * as path from "@tauri-apps/api/path";
import { isAbsolute } from "pathe";

import type {
	GrpcRequestSpec,
	HttpRequestSpec,
	JavaScriptRequestSpec,
	PostgresRequestSpec,
	RequestSpec,
	SqliteRequestSpec,
} from "@moment/api-collab";
import { Logger } from "@moment/logging";
import type { ClientStoreV2 } from "@moment/reboot-differential";

import { isQueryableFile } from "../attachments/attachments.atom";
import { $csvBindings } from "../attachments/csv-bindings.atom";
import { tableNameFromFilename } from "../attachments/csv-store-sync";
import { $selectedAwsRegion } from "../aws";
import { $documentsList } from "../document-list/document-list.atom";
import { $selectedEnvironmentName } from "../environments";
import type { EnvironmentVars } from "../environments/environments.types";
import { assertSafeRelativePath, getAttachmentsDirPath } from "../paths/paths.internal";
import { $documentSecretsproxyContext, type SecretsproxyContext } from "../secretsproxy";
import { store } from "../store";
import { ensureAwsFetchInterceptor } from "./aws-fetch-interceptor";
import { ensureGeneralFetchInterceptor, setCurrentFetchContext } from "./general-fetch-interceptor";
import type {
	EnvFetchResponse,
	GrpcResponse,
	GrpcResponseMessage,
	HttpResponse,
	JavaScriptResponse,
	PostgresColumn,
	PostgresResponse,
	QueryResponse,
	QueryTiming,
	SqliteResponse,
} from "./request-page.types";
import { substituteString } from "./substitute-variables";

export type ExecuteRequestResult = {
	response: QueryResponse;
	timing: QueryTiming;
};

const logger = new Logger("execute-request");

const waitForDocumentsListReady = async (): Promise<void> => {
	if (store.get($documentsList).status !== "initializing") {
		return;
	}

	await new Promise<void>((resolve) => {
		let done = false;
		const finish = () => {
			if (done) {
				return;
			}
			done = true;
			unsub();
			resolve();
		};
		const unsub = store.sub($documentsList, () => {
			if (store.get($documentsList).status !== "initializing") {
				finish();
			}
		});

		if (store.get($documentsList).status !== "initializing") {
			finish();
		}

		setTimeout(finish, 5000);
	});
};

// =============================================================================
// Execute Request - Unified Entry Point
// =============================================================================

/**
 * Execute a request and return the response with timing.
 *
 * Routes based on request type:
 *  - "http"     → Rust (execute_env_fetch or execute_kube_request)
 *  - "postgres" → Rust (execute_sql_query)
 *
 * Environment variables are resolved on the Rust side from the selected
 * environment name and optional document id.
 *
 * @param request - The unresolved RequestSpec (variables may still be present)
 * @param params  - Call parameters for substitution
 * @param abortController - AbortController for cancellation
 * @param envVars - Environment variables for JS-side substitution (used by JavaScript requests)
 */
export const executeRequest = async (
	request: RequestSpec,
	params: Record<string, string>,
	abortController: AbortController,
	envVars: EnvironmentVars = {},
	environmentName?: string,
	documentId?: string,
	storeV2?: ClientStoreV2
): Promise<ExecuteRequestResult> => {
	const selectedEnvironmentName = environmentName ?? (await store.get($selectedEnvironmentName));

	if (request.type === "postgres") {
		return executePostgresRequest(
			request,
			params,
			abortController,
			selectedEnvironmentName,
			documentId
		);
	}

	if (request.type === "sqlite") {
		return executeSqliteRequest(
			request,
			params,
			abortController,
			selectedEnvironmentName,
			documentId,
			storeV2
		);
	}

	if (request.type === "javascript") {
		return executeJavaScriptRequest(request, envVars, params, abortController, documentId);
	}

	if (request.type === "grpc") {
		return executeGrpcRequest(
			request,
			params,
			abortController,
			selectedEnvironmentName,
			documentId
		);
	}

	// HTTP (default)
	if (request.auth?.type === "kubernetes") {
		if (request.body?.mode === "form") {
			throw new Error("Multipart form body is not supported for Kubernetes requests");
		}
		return executeKubernetesRequest(request, params, selectedEnvironmentName, documentId);
	}

	return executeHttpRequest(request, params, selectedEnvironmentName, documentId);
};

// =============================================================================
// HTTP Request Execution (via Rust execute_env_fetch)
// =============================================================================

/**
 * Resolve the workspace/profile/base URL needed when a request references a
 * remote workspace secret via `{{env.NAME}}`. The workspace is the document's remote
 * workspace when available; otherwise falls back to the user's
 * `$selectedSecretsWorkspace` pick (used while iterating on a local doc
 * before sharing it). Returns undefined only when neither is available — in
 * that case Rust errors out with a clear "SecretsProxy not configured"
 * message if the request actually needs it.
 *
 * The profile follows the current env selection unless the user has set a
 * secrets-profile override.
 *
 * Exported so gRPC request pages (which don't go through `invokeExecuteEnvFetch`)
 * can reuse the same resolution logic before calling `grpc_execute` /
 * `grpc_reflect` / `grpc_stream_start`.
 */
export const resolveSecretsproxyContext = async (
	documentId: string | undefined,
	environmentName: string
): Promise<SecretsproxyContext | undefined> => {
	if (documentId) {
		await waitForDocumentsListReady();
	}

	return await store.get($documentSecretsproxyContext({ documentId, environmentName }));
};

/**
 * Invoke the Rust `execute_env_fetch` command with secretsproxy context
 * populated for the active document/profile. Both request pages and the
 * general fetch interceptor (code cells, JS requests) funnel through here so
 * proxy-routing behavior is identical across call sites.
 */
export const invokeExecuteEnvFetch = async (
	request: HttpRequestSpec,
	params: Record<string, string> | undefined,
	environmentName: string,
	documentId?: string
): Promise<EnvFetchResponse> => {
	const secretsproxy = await resolveSecretsproxyContext(documentId, environmentName);
	return invoke<EnvFetchResponse>("execute_env_fetch", {
		input: {
			request,
			params,
			environment_name: environmentName,
			document_id: documentId,
			secretsproxy,
		},
	});
};

const executeHttpRequest = async (
	request: HttpRequestSpec,
	params: Record<string, string>,
	environmentName: string,
	documentId?: string
): Promise<ExecuteRequestResult> => {
	const startedAt = Date.now();

	logger.debug("Executing HTTP request via Rust", {
		method: request.method,
		url: request.url,
	});

	const response = await invokeExecuteEnvFetch(request, params, environmentName, documentId);

	const endedAt = Date.now();
	const durationMs = response.duration_ms;
	const timing: QueryTiming = { startedAt, endedAt, durationMs };

	return convertRustResponse(response, timing);
};

// =============================================================================
// Kubernetes Request Execution (via Rust execute_kube_request)
// =============================================================================

const executeKubernetesRequest = async (
	request: HttpRequestSpec,
	params: Record<string, string>,
	environmentName: string,
	documentId?: string
): Promise<ExecuteRequestResult> => {
	const startedAt = Date.now();

	logger.debug("Executing Kubernetes request via Rust", {
		method: request.method,
		url: request.url,
	});

	// The Rust execute_kube_request wrapper accepts the full HttpRequestSpec
	// and handles substitution internally
	const response = await invoke<EnvFetchResponse>("execute_kube_request", {
		request,
		params,
		environment_name: environmentName,
		document_id: documentId,
	});

	const endedAt = Date.now();
	const durationMs = response.duration_ms;
	const timing: QueryTiming = { startedAt, endedAt, durationMs };

	return convertRustResponse(response, timing);
};

// =============================================================================
// JavaScript Request Execution (runs user JS code with AWS SDK v3)
//
// The user writes JavaScript that imports AWS SDK v3 clients from
// cdn.skypack.dev and uses env._aws.credentials + env._aws.region.
//
// Two fetch interceptors are installed before running user code:
//  1. AWS interceptor — routes *.amazonaws.com through Rust for SigV4 signing.
//  2. General interceptor — routes all other HTTP(S) through Rust to bypass CORS.
// =============================================================================

const executeJavaScriptRequest = async (
	request: JavaScriptRequestSpec,
	envVars: EnvironmentVars,
	params: Record<string, string>,
	abortController: AbortController,
	documentId?: string
): Promise<ExecuteRequestResult> => {
	const startedAt = Date.now();

	logger.debug("Executing JavaScript code request");

	// Ensure fetch interceptors are installed:
	// 1. AWS interceptor first — routes *.amazonaws.com through Rust for SigV4 signing.
	// 2. General interceptor second — routes all other HTTP(S) through Rust to bypass CORS.
	// Order matters: the general interceptor wraps the AWS one, so AWS URLs
	// are bypassed by the general interceptor and handled by the AWS one.
	ensureAwsFetchInterceptor();
	ensureGeneralFetchInterceptor();

	// Build the env._aws object with dummy credentials (real signing
	// happens in the fetch interceptor) and the selected region.
	const region = (await store.get($selectedAwsRegion)) ?? "us-east-1";
	const envAws = {
		credentials: { accessKeyId: "_moment_proxy", secretAccessKey: "_moment_proxy" },
		region,
	};

	const timeoutMs = request.timeoutMs;

	// Substitute {{params.X}} and {{env.X}} in the code before building the function.
	const resolvedCode = substituteString(request.code, envVars, params);

	// Build an AsyncFunction from the user's code.
	// The code can use `env` (with env._aws), `fetch`, and `await`.
	const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
	const fn = new AsyncFunction("env", "fetch", resolvedCode) as (
		env: {
			_aws: typeof envAws;
		},
		fetch: typeof globalThis.fetch
	) => Promise<unknown>;

	// Race execution against timeout and abort
	const timeoutId = setTimeout(() => abortController.abort("timeout"), timeoutMs);

	// Publish the documentId to the fetch interceptor so any fetch() the
	// user's code triggers (directly or via a library like Octokit) can
	// resolve the workspace/profile needed for remote `env.NAME` refs.
	// The disposer restores the previous context in finally so nested or
	// concurrent cell runs don't leak state.
	const restoreFetchContext = setCurrentFetchContext({ documentId });

	try {
		if (abortController.signal.aborted) {
			throw new DOMException("The operation was aborted", "AbortError");
		}

		const execPromise = fn({ _aws: envAws }, globalThis.fetch);

		const abortPromise = new Promise<never>((_, reject) => {
			if (abortController.signal.aborted) {
				reject(new DOMException("The operation was aborted", "AbortError"));
				return;
			}
			abortController.signal.addEventListener(
				"abort",
				() => reject(new DOMException("The operation was aborted", "AbortError")),
				{ once: true }
			);
		});

		const result = await Promise.race([execPromise, abortPromise]);

		const endedAt = Date.now();
		const durationMs = endedAt - startedAt;
		const timing: QueryTiming = { startedAt, endedAt, durationMs };

		// Serialize the return value as the response body.
		// When user code doesn't explicitly return a value, result is undefined.
		// JSON.stringify(undefined) returns the JS value undefined (not a string),
		// so we handle null/undefined explicitly to produce a clean response.
		let parsedBody: unknown;
		let bodySize = 0;
		if (result == null) {
			parsedBody = null;
		} else {
			try {
				const json = JSON.stringify(result, null, 2);
				bodySize = json.length;
				parsedBody = JSON.parse(json);
			} catch {
				const str = String(result);
				bodySize = str.length;
				parsedBody = str;
			}
		}

		const response: JavaScriptResponse = {
			kind: "javascript",
			parsedBody,
			bodySize,
			durationMs: durationMs,
		};

		return { response, timing };
	} catch (err) {
		if (err instanceof DOMException && err.name === "AbortError") {
			throw err;
		}
		const rawMessage = err instanceof Error ? err.message : String(err);
		throw new Error(rawMessage);
	} finally {
		clearTimeout(timeoutId);
		restoreFetchContext();
	}
};

// =============================================================================
// Postgres Request Execution (via Rust execute_sql_query)
//
// The Rust command handles everything: env var resolution, variable
// substitution, connection pooling, query execution, row decoding with
// text fallback, and credential scrubbing.
//
// JS is a thin pass-through that maps the Rust response to our types
// and adds timeout/abort support.
// =============================================================================

/** Response shape from the Rust execute_sql_query command. */
type RustSqlQueryResponse = {
	columns: Array<{ name: string; data_type: string }>;
	rows: Array<Record<string, unknown>>;
	row_count: number;
	command: string;
	duration_ms: number;
};

/**
 * Strip credentials (userinfo) from a database URL so it can be safely
 * included in error messages shown to the user.  Defense-in-depth: Rust
 * performs primary scrubbing, but we also scrub on the JS side.
 */
const safeUrlForDisplay = (url: string): string => {
	try {
		const parsed = new URL(url);
		parsed.username = "";
		parsed.password = "";
		return parsed.toString();
	} catch {
		return "<connection url>";
	}
};

/**
 * Scrub any occurrence of the connection string (or its credentials) from an
 * error message so sensitive data is never surfaced to the UI / logs.
 *
 * Defense-in-depth: Rust performs primary scrubbing, this is a safety net.
 */
const scrubConnectionString = (message: string, connectionString: string): string => {
	if (!connectionString) {
		return message;
	}

	let scrubbed = message;
	if (scrubbed.includes(connectionString)) {
		scrubbed = scrubbed.replaceAll(connectionString, safeUrlForDisplay(connectionString));
	}

	try {
		const parsed = new URL(connectionString);
		if (parsed.password) {
			scrubbed = scrubbed.replaceAll(parsed.password, "***");
		}
		if (parsed.username) {
			scrubbed = scrubbed.replaceAll(parsed.username, "***");
		}
	} catch {
		// Not a parseable URL — nothing more to scrub
	}

	return scrubbed;
};

const executePostgresRequest = async (
	request: PostgresRequestSpec,
	callParams: Record<string, string>,
	abortController: AbortController,
	environmentName: string,
	documentId?: string
): Promise<ExecuteRequestResult> => {
	const startedAt = Date.now();

	logger.debug("Executing Postgres query via Rust execute_sql_query");

	// Enforce timeoutMs: race the SQL execution against a timer.
	// The Rust command does not support cancellation from JS, so this is a
	// JS-level race that unblocks the UI on timeout.  The underlying query
	// may continue on the server.
	const timeoutMs = request.timeoutMs;
	const timeoutId = setTimeout(() => {
		abortController.abort("timeout");
	}, timeoutMs);

	try {
		if (abortController.signal.aborted) {
			throw new DOMException("The operation was aborted", "AbortError");
		}

		// Convert the merged params Record back to Kv[] for the Rust command.
		// `callParams` already contains spec-level params merged with caller-
		// supplied overrides (caller wins), so we send it as-is.  This ensures
		// that `query({ request: "myPgQuery", params: { x: "1" } })` from code
		// cells can substitute {{params.x}} in the SQL / connection string.
		const paramsKv = Object.entries(callParams).map(([key, value]) => ({
			key,
			value,
			enabled: true,
		}));

		// Invoke the Rust command.  Rust handles:
		// - env var resolution (from the selected environment)
		// - variable substitution (connection string + SQL)
		// - URL normalization
		// - connection pooling
		// - query execution + row decoding
		// - credential scrubbing on errors
		const sqlPromise = invoke<RustSqlQueryResponse>("execute_sql_query", {
			input: {
				connection_string: request.connectionString,
				sql: request.sql,
				params: paramsKv,
				environment_name: environmentName,
				document_id: documentId,
				timeout_ms: request.timeoutMs,
				dialect: "postgres",
			},
		});

		// Listen for abort while the query is in-flight
		const abortPromise = new Promise<never>((_, reject) => {
			if (abortController.signal.aborted) {
				reject(new DOMException("The operation was aborted", "AbortError"));
				return;
			}
			abortController.signal.addEventListener(
				"abort",
				() => reject(new DOMException("The operation was aborted", "AbortError")),
				{ once: true }
			);
		});

		const result = await Promise.race([sqlPromise, abortPromise]);

		const endedAt = Date.now();

		// Map Rust response to our PostgresResponse type
		const columns: PostgresColumn[] = result.columns.map((col) => ({
			name: col.name,
			dataType: col.data_type,
		}));

		const response: PostgresResponse = {
			kind: "postgres",
			columns,
			rows: result.rows,
			rowCount: result.row_count,
			command: result.command,
			durationMs: result.duration_ms,
		};

		const timing: QueryTiming = { startedAt, endedAt, durationMs: result.duration_ms };
		return { response, timing };
	} catch (err) {
		// Defense-in-depth: scrub credentials from any error that leaks through
		if (err instanceof DOMException && err.name === "AbortError") {
			throw err;
		}
		const rawMessage = err instanceof Error ? err.message : String(err);
		throw new Error(scrubConnectionString(rawMessage, request.connectionString));
	} finally {
		clearTimeout(timeoutId);
	}
};

// =============================================================================
// SQLite Request Execution (via Rust execute_sqlite_query)
// =============================================================================

/**
 * Determines whether a SQLite request should use store-backed materialization.
 * CSV files and empty file paths use the store v2 tables when a store is available.
 */
function shouldUseStoreMaterialization(
	filePath: string,
	storeV2: ClientStoreV2 | undefined,
	documentId: string | undefined
): boolean {
	if (!storeV2 || !documentId) {
		return false;
	}
	const trimmed = filePath.trim();
	if (trimmed === "") {
		return true;
	}
	return isQueryableFile(trimmed);
}

/**
 * Build materialized table payloads from the store v2.
 * Each store table becomes a named SQLite table.
 * If a specific CSV filePath was selected, that table is also aliased as "data" for backward compat.
 */
function buildMaterializedTables(
	storeV2: ClientStoreV2,
	documentId: string,
	selectedFilePath: string
): Array<{ name: string; columns: string[]; rows: Record<string, unknown>[] }> {
	const tables: Array<{
		name: string;
		columns: string[];
		rows: Record<string, unknown>[];
	}> = [];

	const tableNames = storeV2.tableNames();
	const bindingsRecord = store.get($csvBindings(documentId));
	const bindingsByTable = new Map(Object.values(bindingsRecord).map((b) => [b.tableName, b]));

	for (const tableName of tableNames) {
		const rows = storeV2.merged(tableName);
		const binding = bindingsByTable.get(tableName);
		const columns = binding
			? binding.sanitizedHeaders
			: rows.length > 0
				? Object.keys(rows[0])
				: [];
		tables.push({ name: tableName, columns, rows });
	}

	// Backward compat: if a specific CSV was selected, alias that table as "data"
	const selectedTable = selectedFilePath.trim()
		? tableNameFromFilename(selectedFilePath.trim().replace(/^.*[\\/]/, ""))
		: undefined;
	if (selectedTable && tableNames.includes(selectedTable)) {
		const existing = tables.find((t) => t.name === selectedTable);
		if (existing && !tableNames.includes("data")) {
			tables.push({ name: "data", columns: existing.columns, rows: existing.rows });
		}
	}

	return tables;
}

const executeSqliteRequest = async (
	request: SqliteRequestSpec,
	callParams: Record<string, string>,
	abortController: AbortController,
	environmentName: string,
	documentId?: string,
	storeV2?: ClientStoreV2
): Promise<ExecuteRequestResult> => {
	const startedAt = Date.now();

	const useStorePath = shouldUseStoreMaterialization(request.filePath, storeV2, documentId);

	if (useStorePath) {
		logger.debug("Executing SQLite query via store-backed materialization");
	} else {
		logger.debug("Executing SQLite query via Rust execute_sqlite_query");
	}

	// For file-based path, resolve against attachments directory
	let resolvedFilePath = request.filePath;
	if (!useStorePath && documentId && resolvedFilePath) {
		if (isAbsolute(resolvedFilePath)) {
			throw new Error("SQLite filePath must be a relative filename, not an absolute path");
		}
		assertSafeRelativePath(resolvedFilePath, "SQLite filePath");
		const attachmentsDir = await getAttachmentsDirPath(documentId);
		resolvedFilePath = await path.join(attachmentsDir, resolvedFilePath);
	}

	const timeoutMs = request.timeoutMs;
	const timeoutId = setTimeout(() => {
		abortController.abort("timeout");
	}, timeoutMs);

	try {
		if (abortController.signal.aborted) {
			throw new DOMException("The operation was aborted", "AbortError");
		}

		const paramsKv = Object.entries(callParams).map(([key, value]) => ({
			key,
			value,
			enabled: true,
		}));

		let sqlPromise: Promise<RustSqlQueryResponse>;

		if (useStorePath) {
			const materializedTables = buildMaterializedTables(
				storeV2!,
				documentId!,
				request.filePath
			);
			sqlPromise = invoke<RustSqlQueryResponse>("execute_sqlite_query_from_tables", {
				input: {
					tables: materializedTables,
					sql: request.sql,
					params: paramsKv,
					environment_name: environmentName,
					document_id: documentId,
					timeout_ms: request.timeoutMs,
				},
			});
		} else {
			sqlPromise = invoke<RustSqlQueryResponse>("execute_sqlite_query", {
				input: {
					file_path: resolvedFilePath,
					sql: request.sql,
					params: paramsKv,
					environment_name: environmentName,
					document_id: documentId,
					timeout_ms: request.timeoutMs,
				},
			});
		}

		const abortPromise = new Promise<never>((_, reject) => {
			if (abortController.signal.aborted) {
				reject(new DOMException("The operation was aborted", "AbortError"));
				return;
			}
			abortController.signal.addEventListener(
				"abort",
				() => reject(new DOMException("The operation was aborted", "AbortError")),
				{ once: true }
			);
		});

		const result = await Promise.race([sqlPromise, abortPromise]);

		const endedAt = Date.now();

		const columns: PostgresColumn[] = result.columns.map((col) => ({
			name: col.name,
			dataType: col.data_type,
		}));

		const response: SqliteResponse = {
			kind: "sqlite",
			columns,
			rows: result.rows,
			rowCount: result.row_count,
			command: result.command,
			durationMs: result.duration_ms,
		};

		const timing: QueryTiming = { startedAt, endedAt, durationMs: result.duration_ms };
		return { response, timing };
	} catch (err) {
		if (err instanceof DOMException && err.name === "AbortError") {
			throw err;
		}
		const rawMessage = err instanceof Error ? err.message : String(err);
		throw new Error(rawMessage);
	} finally {
		clearTimeout(timeoutId);
	}
};

// =============================================================================
// gRPC Request Execution (via Rust grpc_execute / grpc_stream_start)
// =============================================================================

/** Response shape from the Rust grpc_execute command. */
type RustGrpcExecuteResponse = {
	response: {
		status_code: number;
		status_message: string;
		body_json: string | null;
		metadata: [string, string][];
		duration_ms: number;
	} | null;
	stream_result: {
		status_code: number;
		status_message: string;
		messages: Array<{ index: number; body_json: string }>;
		response_metadata: [string, string][];
		trailers: [string, string][];
		duration_ms: number;
	} | null;
	call_type: string;
};

const executeGrpcRequest = async (
	request: GrpcRequestSpec,
	callParams: Record<string, string>,
	abortController: AbortController,
	environmentName: string,
	documentId?: string
): Promise<ExecuteRequestResult> => {
	// Resolve the secretsproxy context the same way HTTP requests do, so a
	// gRPC call that references a remote `{{env.NAME}}` anywhere in its
	// URL / metadata / auth token is routed through the proxy service.
	// The Rust `grpc_execute` command only consumes this context when the
	// request actually references a proxied secret; otherwise it's ignored.
	const secretsproxy = await resolveSecretsproxyContext(documentId, environmentName);
	const startedAt = Date.now();

	logger.debug("Executing gRPC request via Rust", {
		url: request.url,
		service: request.service,
		method: request.method,
	});

	const timeoutMs = request.timeoutMs;
	const timeoutId = setTimeout(() => {
		abortController.abort("timeout");
	}, timeoutMs);

	try {
		if (abortController.signal.aborted) {
			throw new DOMException("The operation was aborted", "AbortError");
		}

		// Build metadata from kvs
		const metadataEntries: [string, string][] = (request.metadata ?? [])
			.filter((kv) => kv.enabled !== false && kv.key)
			.map((kv) => [kv.key, kv.value]);

		// TLS is auto-detected from URL (port 443 / https scheme).
		// Only pass explicit config if the user has set custom cert paths.
		const hasCustomTls =
			request.tlsCaCertPath || request.tlsClientCertPath || request.tlsClientKeyPath;
		const tlsConfig = hasCustomTls
			? {
					ca_cert_path: request.tlsCaCertPath ?? null,
					client_cert_path: request.tlsClientCertPath ?? null,
					client_key_path: request.tlsClientKeyPath ?? null,
					skip_verify: request.tlsSkipVerify,
				}
			: null;

		// Build params from caller-supplied params + user-defined vars
		const paramsKv = [
			...(request.vars ?? [])
				.filter((kv) => kv.enabled !== false && kv.key)
				.map((kv) => ({ key: kv.key, value: kv.value, enabled: true })),
			...Object.entries(callParams).map(([key, value]) => ({
				key,
				value,
				enabled: true,
			})),
		];

		const grpcPromise = invoke<RustGrpcExecuteResponse>("grpc_execute", {
			input: {
				url: request.url,
				service: request.service,
				method: request.method,
				message: request.message,
				metadata: metadataEntries,
				proto_files: request.protoFiles,
				import_paths: request.importPaths,
				use_reflection: request.useReflection,
				tls_config: tlsConfig,
				auth: request.auth?.type !== "none" ? request.auth : null,
				timeout_ms: request.timeoutMs,
				environment_name: environmentName,
				document_id: documentId,
				params: paramsKv,
				secretsproxy,
			},
		});

		const abortPromise = new Promise<never>((_, reject) => {
			if (abortController.signal.aborted) {
				reject(new DOMException("The operation was aborted", "AbortError"));
				return;
			}
			abortController.signal.addEventListener(
				"abort",
				() => reject(new DOMException("The operation was aborted", "AbortError")),
				{ once: true }
			);
		});

		const result = await Promise.race([grpcPromise, abortPromise]);

		const endedAt = Date.now();

		if (result.call_type === "unary" && result.response) {
			const body = result.response.body_json;
			let parsedBody: unknown = body;
			if (body) {
				try {
					parsedBody = JSON.parse(body);
				} catch {
					// Keep as string
				}
			}
			const bodySize = body?.length ?? 0;

			// If error status with no body, use empty messages so the UI shows
			// the error status instead of rendering a null body as blank text.
			const isError = result.response.status_code !== 0;
			const messages = isError && !body ? [] : [{ index: 0, body: parsedBody, bodySize }];

			const response: GrpcResponse = {
				kind: "grpc",
				statusCode: result.response.status_code,
				statusMessage: result.response.status_message,
				messages,
				responseMetadata: result.response.metadata,
				trailers: [],
				durationMs: result.response.duration_ms,
				streamType: "unary",
			};

			const timing: QueryTiming = {
				startedAt,
				endedAt,
				durationMs: result.response.duration_ms,
			};
			return { response, timing };
		}

		if (result.stream_result) {
			const messages: GrpcResponseMessage[] = result.stream_result.messages.map((msg) => {
				let parsedBody: unknown = msg.body_json;
				try {
					parsedBody = JSON.parse(msg.body_json);
				} catch {
					// Keep as string
				}
				return {
					index: msg.index,
					body: parsedBody,
					bodySize: msg.body_json.length,
				};
			});

			const response: GrpcResponse = {
				kind: "grpc",
				statusCode: result.stream_result.status_code,
				statusMessage: result.stream_result.status_message,
				messages,
				responseMetadata: result.stream_result.response_metadata,
				trailers: result.stream_result.trailers,
				durationMs: result.stream_result.duration_ms,
				streamType: "server",
			};

			const timing: QueryTiming = {
				startedAt,
				endedAt,
				durationMs: result.stream_result.duration_ms,
			};
			return { response, timing };
		}

		throw new Error("Unexpected gRPC response format");
	} catch (err) {
		if (err instanceof DOMException && err.name === "AbortError") {
			throw err;
		}
		const rawMessage = err instanceof Error ? err.message : String(err);
		throw new Error(rawMessage);
	} finally {
		clearTimeout(timeoutId);
	}
};

// =============================================================================
// Shared HTTP response conversion
// =============================================================================

const convertAuthHint = (
	hint: NonNullable<EnvFetchResponse["auth_hint"]>
): NonNullable<HttpResponse["auth_hint"]> => ({
	provider: hint.provider,
	...(hint.cluster_name ? { cluster_name: hint.cluster_name } : {}),
	...(hint.region ? { region: hint.region } : {}),
	...(hint.account_id ? { account_id: hint.account_id } : {}),
});

const convertRustResponse = (
	response: EnvFetchResponse,
	timing: QueryTiming
): ExecuteRequestResult => {
	// Parse response headers as tuples
	const headersArray: [string, string][] = [];
	for (const [key, value] of Object.entries(response.headers)) {
		headersArray.push([key, value]);
	}

	// Handle binary responses
	if (response.body_base64) {
		const binarySize = Math.floor((response.body_base64.length * 3) / 4);
		return {
			response: {
				kind: "http" as const,
				status: response.status,
				statusText: response.status_text,
				ok: response.status >= 200 && response.status < 300,
				url: response.resolved_url,
				headers: headersArray,
				parsedBody: response.body_base64,
				bodyEncoding: "base64" as const,
				bodySize: binarySize,
				...(response.auth_hint ? { auth_hint: convertAuthHint(response.auth_hint) } : {}),
			},
			timing,
		};
	}

	// Try to parse as JSON, fall back to raw string
	const bodyStr = response.body ?? "";
	let parsedBody: unknown = bodyStr;
	try {
		parsedBody = JSON.parse(bodyStr);
	} catch {
		// Keep as string
	}

	return {
		response: {
			kind: "http" as const,
			status: response.status,
			statusText: response.status_text,
			ok: response.status >= 200 && response.status < 300,
			url: response.resolved_url,
			headers: headersArray,
			parsedBody,
			bodyEncoding: "utf-8" as const,
			bodySize: bodyStr.length,
			...(response.auth_hint ? { auth_hint: convertAuthHint(response.auth_hint) } : {}),
		},
		timing,
	};
};
